import * as React from 'react';

import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { abbrNum } from 'utils/helper/common';
import { renderReactTooltipToElement } from 'components/graphs/echarts/util/echarts-react-tooltip';

import type {
  BarGraphEndpointDataProp,
  BarGraphFormatterParam,
  BarGraphFormatterParamSize,
  BarGraphTooltip,
  DOMRect
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('../bar-graph.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'bar_graph';
const ENGAGEMENT_RATE = 'engagementRate';

export const renderLink = (url: string | undefined | null, dataProp: string): React.ReactNode => {
  if (isNil(url)) {
    return '';
  }

  const isVideo = dataProp.includes('video');
  const linkText = __t(`${MSG_KEY}.labels.view_item`, {
    type: isVideo ? 'video' : 'post'
  });

  return (
    <a
      href={url}
      className="bar-graph__tooltip-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {linkText}
    </a>
  );
};

export const getFormattedTooltip = (
  param: BarGraphFormatterParam,
  channel: string,
  dataProp: BarGraphEndpointDataProp,
  url?: string | null
): React.ReactElement => {
  const channelLabel = __t(`${COMMON_MSG_KEY}.social.${channel}`) || '';
  const propLabel = __t(`${COMMON_MSG_KEY}.basics.${snakeCase(dataProp)}`) || '';
  const percentSign = dataProp === ENGAGEMENT_RATE ? '%' : '';

  return (
    <div className="bar-graph__tooltip">
      <div className="bar-graph__tooltip-title">
        <div>
          {channelLabel}
        </div>
        <div className="bar-graph__tooltip-date">
          ({getDisplayDate(param.name)})
        </div>
      </div>
      <div className="bar-graph__tooltip-value">
        {propLabel}: {abbrNum(param.value)}{percentSign}
      </div>
      {renderLink(url, dataProp)}
    </div>
  );
};

export const getTooltip = (channel: string, dataProp: BarGraphEndpointDataProp, url?: string | null): BarGraphTooltip => ({
  padding: [10, 14, 6, 14],
  backgroundColor: '#fff',
  textStyle: {
    fontFamily: 'Hind',
    color: '#3a3a3a',
    lineHeight: 20
  },
  position: (
    mousePosition: Array<number>,
    param: BarGraphFormatterParam | undefined | null,
    dom: HTMLElement | undefined | null,
    rect: DOMRect | undefined | null,
    size: BarGraphFormatterParamSize
  ): Array<number> => {
    const x = mousePosition[0] - (size.contentSize[0] / 2);
    const y = (mousePosition[1] - size.contentSize[1]) - 8;

    return [x, y];
  },

  formatter: (param: BarGraphFormatterParam): HTMLElement =>
    renderReactTooltipToElement('bar-graph', getFormattedTooltip(param, channel, dataProp, url)),
  extraCssText: 'box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.2); border-color: #e0e0e0;'
});
